import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { listBlogPosts } from "@/lib/blog.functions";
import { autoExcerpt, readingMinutes } from "@/lib/blog-shared";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, collectionSchema } from "@/lib/schema";
import { DefaultRouteError, NotFoundView } from "@/components/RouteFallbacks";
import { NewPagesLinks, NEW_MARKET_PAGES } from "@/components/NewPagesLinks";

const postsQueryOptions = queryOptions({
  queryKey: ["blog-posts"],
  queryFn: () => listBlogPosts(),
  staleTime: 5 * 60 * 1000,
});


const URL = "https://palstars.com/blog";
const TITLE = "Palworld TCG Blog — News, Prices & Deck Analysis";
const DESCRIPTION =
  "The Pal Stars Palworld blog: Palworld OFFICIAL CARD GAME set news, card price movement, deck analysis and collecting advice, written against our own card and price data.";

const FAQ: FaqItem[] = [
  {
    q: "What is the Pal Stars Palworld blog?",
    a: "It is the editorial side of Pal Stars: Palworld TCG set news, card price movement, deck analysis and collecting advice. Every article is written against the same card database and price data that powers the Pal Stars price chart, so figures in an article can be checked on the site.",
  },
  {
    q: "How often are new Palworld blog posts published?",
    a: "Posts go up whenever there is real news — a set announcement, a rules update, a tournament result or a notable price move. Guides and tools are refreshed weekly, so the blog covers what changed rather than repeating the evergreen guides.",
  },
  {
    q: "Is the Pal Stars blog official Palworld news?",
    a: "No. Pal Stars is an unofficial fan site. Palworld is developed by Pocketpair and the card game is published by Bushiroad. Anything unconfirmed is labelled as unconfirmed in the article itself.",
  },
  {
    q: "Do blog posts contain affiliate links?",
    a: "Some do. Where an article links to a marketplace listing, Pal Stars may earn a commission through the eBay Partner Network at no extra cost to you, and the article carries a disclosure.",
  },
];

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG Blog — Pal Stars" },
      { property: "og:description", content: "Palworld card game news, price movement and deck analysis from the Pal Stars database." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Blog", "/blog"],
      ]),
      collectionSchema({ name: "Palworld TCG Blog", description: DESCRIPTION, path: "/blog", items: [] }),
      faqSchema(FAQ, URL),
    ],
  }),
  component: BlogIndex,
  loader: ({ context }) => context.queryClient.ensureQueryData(postsQueryOptions),
  errorComponent: DefaultRouteError,
  notFoundComponent: () => <NotFoundView />,
});

function BlogIndex() {
  const { data: posts = [] } = useSuspenseQuery(postsQueryOptions);


  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars · editorial</p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">Palworld TCG Blog</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          News, price movement and deck analysis for the Palworld OFFICIAL CARD GAME, written against the same card
          database and marketplace data that powers the{" "}
          <Link to="/prices" className="font-semibold text-primary hover:underline">
            Pal Stars price chart
          </Link>
          .
        </p>
        <NewPagesLinks pages={NEW_MARKET_PAGES} />
      </header>

      {posts.length === 0 ? (

        <section className="glass rounded-2xl p-6">
          <h2 className="text-xl font-bold">First articles are on the way</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Nothing is published yet. In the meantime, the deep reference material lives in the{" "}
            <Link to="/guides" className="font-semibold text-primary hover:underline">
              Palworld guides library
            </Link>{" "}
            and today's card values are on the{" "}
            <Link to="/prices" className="font-semibold text-primary hover:underline">
              price chart
            </Link>
            .
          </p>
        </section>
      ) : (
        <section>
          <h2 className="sr-only">Latest Palworld articles</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {posts.map((p) => (
              <article key={p.id} className="glass overflow-hidden rounded-2xl">
                {p.cover_image ? (
                  <Link to="/blog/$slug" params={{ slug: p.slug }} className="block aspect-[16/9] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.cover_alt ?? p.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </Link>
                ) : null}
                <div className="p-5">
                  <p className="text-[11px] uppercase tracking-widest text-primary">
                    {p.tag} · {readingMinutes(p.body)} min read
                  </p>
                  <h3 className="mt-2 text-lg font-bold">
                    <Link to="/blog/$slug" params={{ slug: p.slug }} className="hover:text-primary">
                      {p.title}
                    </Link>
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{autoExcerpt(p)}</p>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="mt-3 inline-block text-sm font-semibold text-primary hover:underline"
                  >
                    Read the article →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Keep reading</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li>
            <Link to="/guides" className="font-semibold text-primary hover:underline">
              Palworld guides &amp; tier lists →
            </Link>
          </li>
          <li>
            <Link to="/prices" className="font-semibold text-primary hover:underline">
              Palworld TCG price chart →
            </Link>
          </li>
          <li>
            <Link to="/marketplace" className="font-semibold text-primary hover:underline">
              Palworld TCG marketplace →
            </Link>
          </li>
          <li>
            <Link to="/cards" className="font-semibold text-primary hover:underline">
              Palworld card database →
            </Link>
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld blog FAQ" />
    </div>
  );
}
